import type { Graph, GraphNode } from '../types';

const COMPARE_KEYS: (keyof GraphNode)[] = ['group', 'label', 'size', 'title', 'flashcardsCompleted'];

const MIN_SIZE = 15;
const MAX_SIZE = 90;

function nodeNeedsUpdate(newNode: GraphNode, currentNode: GraphNode): boolean {
  for (const key of COMPARE_KEYS) {
    if (newNode[key] !== currentNode[key]) return true;
  }
  const nc = newNode.color;
  const cc = currentNode.color;
  if (nc !== cc) {
    if (typeof nc !== typeof cc) return true;
    if (typeof nc === 'object' && nc !== null && cc !== null && typeof cc === 'object') {
      if (nc.border !== cc.border || nc.background !== cc.background) return true;
    } else {
      return true;
    }
  }
  return false;
}

function applyDynamicSizes(nodes: GraphNode[], edges: Graph['edges']): GraphNode[] {
  if (edges.length === 0) return nodes;

  const degreeMap = new Map<number | string, number>();
  for (const edge of edges) {
    degreeMap.set(edge.from, (degreeMap.get(edge.from) || 0) + 1);
    degreeMap.set(edge.to, (degreeMap.get(edge.to) || 0) + 1);
  }

  const maxDegree = Math.max(...Array.from(degreeMap.values()));

  return nodes.map(node => {
    const degree = degreeMap.get(node.id) || 0;
    const ratio = degree / maxDegree;
    const size = MIN_SIZE + (ratio * ratio) * (MAX_SIZE - MIN_SIZE);
    return { ...node, size };
  });
}

export function updateNodes(newGraph: Graph, networkRef: React.RefObject<any>): void {
  const sizedNodes = applyDynamicSizes(newGraph.nodes, newGraph.edges);
  const currentNodes: GraphNode[] = networkRef.current.body.data.nodes.get();
  const currentNodeMap = new Map(currentNodes.map(node => [node.id, node]));

  const newNodeIds = new Set(sizedNodes.map(node => node.id));
  const nodesToAdd = sizedNodes.filter(node => !currentNodeMap.has(node.id));

  const nodesToRemove = currentNodes.filter(node => !newNodeIds.has(node.id));

  const nodesToUpdate = sizedNodes.filter(node => {
    const current = currentNodeMap.get(node.id);
    return current && nodeNeedsUpdate(node, current);
  });

  if (nodesToAdd.length > 0) {
    networkRef.current.body.data.nodes.add(nodesToAdd);
  }
  if (nodesToRemove.length > 0) {
    networkRef.current.body.data.nodes.remove(nodesToRemove.map(n => n.id));
  }
  if (nodesToUpdate.length > 0) {
    networkRef.current.body.data.nodes.update(nodesToUpdate);
  }
}
